import { collection, addDoc, query, where, getDocs, DocumentData, QuerySnapshot, deleteDoc, doc } from 'firebase/firestore';
import { db } from '../lib/firebase';
import { getAuth } from 'firebase/auth';
import { getApiUrl } from '../config/api';
import type { Trade, TradingSetup } from '../types';

interface TradingPlanCondition {
  id: string;
  name: string;
}

interface TradingPlan {
  id: string;
  name: string;
  color: string;
  conditions: TradingPlanCondition[];
  createdAt: any;
  userId: string;
}

export type AISuggestion = {
  id: string;
  type: 'ADD_CONDITION' | 'SET_RISK_LIMIT' | 'NAVIGATE_TO_CONDITIONS' | 'NAVIGATE_TO_MODAL' | 'NAVIGATE_TO_PAGE' | 'ADD_RECOMMENDATION';
  title: string;
  description: string;
  data: {
    name?: string;
    description?: string;
    category?: string;
    percentage?: number;
    action?: string;
    path?: string;
    content?: string;
    type?: string;
  };
};

export interface AIAnalysis {
  id?: string;
  tradeId: string;
  userId: string;
  timestamp: string;
  riskManagement: {
    note: number;
    synthesis: string;
  };
  performance: {
    note: number;
    synthesis: string;
  };
  sessionAnalysis: {
    note: number;
    synthesis: string;
  };
  moodAnalysis: {
    note: number;
    synthesis: string;
  };
  setupAnalysis: {
    note: number;
    synthesis: string;
  };
  pairAnalysis: {
    note: number;
    synthesis: string;
  };
  timeframeAnalysis: {
    note: number;
    synthesis: string;
  };
  dayOfWeekAnalysis: {
    note: number;
    synthesis: string;
  };
  biggestGainAnalysis: {
    note: number;
    synthesis: string;
  };
  biggestLossAnalysis: {
    note: number;
    synthesis: string;
  };
  streakImpact: {
    note: number;
    synthesis: string;
  };
  overallScore: number;
  summary: string;
  recommendations: string[];
  suggestions?: AISuggestion[];
  profileType?: string;
  successRate?: number;
  totalTrades?: number;
  bestSession?: string;
  bestSessionRate?: number;
  otherSessionRate?: number;
  bestSetup?: string;
  averageDuration?: string;
  username?: string;
  metrics?: {
    winRate: number;
    profitFactor: number;
    avgWinLoss: number;
    recoveryFactor: number;
    maxDrawdown: number;
    consistency: number;
  };
}



class AIAnalysisService {
  async getTradeAnalysis(tradeId: string): Promise<AIAnalysis | null> {
    const auth = getAuth();
    const user = auth.currentUser;

    if (!user) {
      throw new Error('Must be logged in to fetch analysis');
    }

    try {
      const q = query(
        collection(db, 'tradeAnalyses'),
        where('tradeId', '==', tradeId),
        where('userId', '==', user.uid)
      );

      const snapshot = await getDocs(q);
      if (snapshot.empty) return null;

      const doc = snapshot.docs[0];
      return { id: doc.id, ...doc.data() } as AIAnalysis;
    } catch (error) {
      // console.error supprimé
      throw new Error('Failed to fetch analysis');
    }
  }

  private async saveAnalysis(tradeId: string, analysis: any): Promise<void> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User must be authenticated');
      }

      // S'assurer que l'analyse a le bon tradeId
      const analysisData: AIAnalysis = {
        ...analysis,
        tradeId, // Ajouter explicitement le tradeId
        userId: user.uid,
        timestamp: new Date().toISOString()
      };

      await addDoc(collection(db, 'tradeAnalyses'), analysisData);
    } catch (error) {
      // console.error supprimé
      throw new Error('Failed to save analysis');
    }
  }

  private async getUserTradingPlans(userId: string): Promise<TradingPlan[]> {
    try {
      const plansRef = collection(db, 'users', userId, 'plans');
      const plansSnapshot = await getDocs(plansRef);
      
      return plansSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data(),
      } as TradingPlan));
    } catch (error) {
      // console.error supprimé
      return [];
    }
  }

  private generateSuggestions(analysis: AIAnalysis, trade: Trade): AISuggestion[] {
    const suggestions: AISuggestion[] = [];

    if (analysis.moodAnalysis.note < 6 || 
        trade.mood === 'STRESSED' || 
        trade.mood === 'FOMO' || 
        trade.mood === 'REVENGE') {
      suggestions.push({
        id: 'emotion-control',
        type: 'ADD_CONDITION',
        title: 'Add Emotional Control Condition',
        description: 'Your emotional state seems to be affecting your trading decisions. Add a condition to prevent trading during high emotional states.',
        data: {
          name: 'No Trading During High Emotions',
          description: 'Avoid taking trades when feeling stressed, FOMO, or revenge trading urges.',
          category: 'PSYCHOLOGICAL',
        },
      });
    }

    if (analysis.riskManagement.note < 6 && trade.riskPercentage > 2) {
      suggestions.push({
        id: 'risk-limit',
        type: 'SET_RISK_LIMIT',
        title: 'Adjust Risk Management',
        description: 'Your current risk per trade seems high given market conditions. Consider limiting risk to 1% per trade.',
        data: {
          percentage: 1,
        },
      });
    }

    if (analysis.sessionAnalysis.note < 6) {
      suggestions.push({
        id: 'review-conditions',
        type: 'NAVIGATE_TO_CONDITIONS',
        title: 'Review Trading Conditions',
        description: 'Multiple areas for improvement identified. Review and update your trading conditions.',
        data: {},
      });
    }

    return suggestions;
  }



  // Crée une analyse par défaut en cas d'erreur
  private createDefaultAnalysis(): AIAnalysis {
    const auth = getAuth();
    const userId = auth.currentUser?.uid || '';
    
    return {
      id: '',
      tradeId: '',
      userId: userId,
      timestamp: new Date().toISOString(),
      riskManagement: { 
        note: 5, 
        synthesis: "Impossible d'analyser la gestion du risque. Veuillez réessayer ultérieurement." 
      },
      performance: { 
        note: 5, 
        synthesis: "Impossible d'analyser la performance. Veuillez réessayer ultérieurement." 
      },
      sessionAnalysis: { 
        note: 5, 
        synthesis: "Impossible d'analyser la session. Veuillez réessayer ultérieurement." 
      },
      moodAnalysis: { 
        note: 5, 
        synthesis: "Impossible d'analyser l'état d'esprit. Veuillez réessayer ultérieurement." 
      },
      setupAnalysis: { 
        note: 5, 
        synthesis: "Impossible d'analyser le setup. Veuillez réessayer ultérieurement." 
      },
      pairAnalysis: { 
        note: 5, 
        synthesis: "Impossible d'analyser la paire. Veuillez réessayer ultérieurement." 
      },
      timeframeAnalysis: { 
        note: 5, 
        synthesis: "Impossible d'analyser le timeframe. Veuillez réessayer ultérieurement." 
      },
      dayOfWeekAnalysis: { 
        note: 5, 
        synthesis: "Impossible d'analyser le jour de la semaine. Veuillez réessayer ultérieurement." 
      },
      biggestGainAnalysis: { 
        note: 5, 
        synthesis: "Impossible d'analyser les gains. Veuillez réessayer ultérieurement." 
      },
      biggestLossAnalysis: { 
        note: 5, 
        synthesis: "Impossible d'analyser les pertes. Veuillez réessayer ultérieurement." 
      },
      streakImpact: { 
        note: 5, 
        synthesis: "Impossible d'analyser l'impact des séries. Veuillez réessayer ultérieurement." 
      },
      overallScore: 5,
      summary: "Une erreur s'est produite lors de l'analyse de ce trade. Veuillez réessayer ultérieurement.",
      recommendations: ["[SETUP] Réessayez l'analyse plus tard pour obtenir des recommandations personnalisées."],
      suggestions: [],
      profileType: 'N/A',
      successRate: 0,
      totalTrades: 0,
      bestSession: 'N/A',
      bestSessionRate: 0,
      otherSessionRate: 0,
      bestSetup: 'N/A',
      averageDuration: 'N/A',
      username: '',
      metrics: {
        winRate: 0,
        profitFactor: 0,
        avgWinLoss: 0,
        recoveryFactor: 0,
        maxDrawdown: 0,
        consistency: 0,
      },
    };
  }

  private async analyzeRisk(trade: Trade): Promise<{
    score: number;
    positionSizing: string;
    stopLossAnalysis: string;
    riskRewardEvaluation: string;
  }> {
    // TO DO: implement risk analysis logic
    return {
      score: 0,
      positionSizing: '',
      stopLossAnalysis: '',
      riskRewardEvaluation: '',
    };
  }

  async analyzeTrade(trade: Trade, onProgress?: (step: string, data: any) => void): Promise<AIAnalysis> {
    try {
      // Si un timestamp de rafraîchissement est présent, ignorer l'analyse existante
      const isRefreshing = 'refreshTimestamp' in trade;
      
      // Vérifier si une analyse existe déjà (sauf si on force le rafraîchissement)
      if (!isRefreshing) {
        const existingAnalysis = await this.getTradeAnalysis(trade.id);
        if (existingAnalysis) {
          return existingAnalysis;
        }
      } else {
        // Si on force le rafraîchissement, supprimer l'analyse existante
        await this.deleteAnalysis(trade.id);
      }

      // Récupérer les plans de trading de l'utilisateur
        const auth = getAuth();
        const user = auth.currentUser;
      if (!user) {
        throw new Error('User must be authenticated');
      }

          const userPlans = await this.getUserTradingPlans(user.uid);

      // Préparer les données pour le backend
      const tradeData = {
              ...trade,
        plans: trade.plans || [],
              availablePlans: userPlans
            };

      // Appeler la nouvelle route d'analyse progressive
      const token = await user.getIdToken();
      const response = await fetch(getApiUrl('/api/analyze-trade'), {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${token}`
        },
        body: JSON.stringify({
          trade: tradeData,
          userPlans: userPlans
        })
      });

      if (!response.ok) {
        throw new Error('Failed to analyze trade');
      }

      // Lire le stream de réponses
      const reader = response.body?.getReader();
      if (!reader) {
        throw new Error('No response stream');
      }

      let finalAnalysis: AIAnalysis | null = null;
      const decoder = new TextDecoder();
      let buffer = ''; // Buffer pour accumuler les données incomplètes

      try {
        while (true) {
          const { done, value } = await reader.read();
          
          if (done) break;
          
          // Ajouter le nouveau chunk au buffer
          buffer += decoder.decode(value);
          
          // Traiter toutes les lignes complètes dans le buffer
          let lineEndIndex;
          while ((lineEndIndex = buffer.indexOf('\n')) !== -1) {
            const line = buffer.slice(0, lineEndIndex).trim();
            buffer = buffer.slice(lineEndIndex + 1);
            
            if (line.startsWith('data: ')) {
              try {
                const jsonStr = line.slice(6);
                const data = JSON.parse(jsonStr);
                // console.log supprimé
                
                // Notifier le progrès si callback fourni
                if (onProgress) {
                  // console.log supprimé
                  onProgress(data.step, data);
                } else {
                  // console.warn supprimé
                }
                
                // Si c'est l'analyse finale, la stocker
                if (data.step === 'final' && data.status === 'completed') {
                  finalAnalysis = {
                    ...data.data,
                    id: '',
        tradeId: trade.id,
                    userId: user.uid,
        timestamp: new Date().toISOString()
      };
                }
              } catch (e) {
                // console.warn supprimé
              }
            }
          }
        }

        // Traiter le reste du buffer s'il contient des données
        if (buffer.trim()) {
          const remainingLines = buffer.split('\n');
          for (const line of remainingLines) {
            if (line.trim().startsWith('data: ')) {
              try {
                const jsonStr = line.trim().slice(6);
                const data = JSON.parse(jsonStr);
                // console.log supprimé
                
                if (onProgress) {
                  onProgress(data.step, data);
                }
                
                if (data.step === 'final' && data.status === 'completed') {
                  finalAnalysis = {
                    ...data.data,
                    id: '',
                    tradeId: trade.id,
                    userId: user.uid,
                    timestamp: new Date().toISOString()
                  };
                }
              } catch (e) {
                // console.warn supprimé
              }
            }
          }
        }
      } finally {
        reader.releaseLock();
      }

      if (!finalAnalysis) {
        throw new Error('No final analysis received');
      }

      // Sauvegarder l'analyse
      await this.saveAnalysis(trade.id, finalAnalysis);
      return finalAnalysis;
      
    } catch (error) {
      // console.error supprimé
      
      // En cas d'erreur, créer une analyse par défaut
      const defaultAnalysis = this.createDefaultAnalysis();
      defaultAnalysis.tradeId = trade.id;
      defaultAnalysis.userId = trade.userId || '';
      
      try {
        await this.saveAnalysis(trade.id, defaultAnalysis);
      } catch (saveError) {
        // console.error supprimé
      }
      
      return defaultAnalysis;
    }
  }

  async deleteAnalysis(tradeId: string): Promise<void> {
    try {
      const auth = getAuth();
      const user = auth.currentUser;
      if (!user) {
        throw new Error('User must be authenticated');
      }

      // Chercher l'analyse existante
      const q = query(
        collection(db, 'tradeAnalyses'),
        where('tradeId', '==', tradeId),
        where('userId', '==', user.uid)
      );

      const snapshot = await getDocs(q);
      if (!snapshot.empty) {
        // Supprimer le document
        const docRef = doc(db, 'tradeAnalyses', snapshot.docs[0].id);
        await deleteDoc(docRef);
      }
    } catch (error) {
      // console.error supprimé
      throw new Error('Failed to delete analysis');
    }
  }
}

export const aiAnalysisService = new AIAnalysisService();
